import React, { useState } from 'react';
import { useLeaderboard } from '../hooks/useGamification';
import GamificationServices from '../services/GamificationServices';
import TierBadge from './TierBadge';

const LeaderboardCard = () => {
  const [selectedModule, setSelectedModule] = useState('');
  const [limit, setLimit] = useState(50);
  
  const { leaderboard, loading, error, refresh } = useLeaderboard({
    limit,
    module: selectedModule || undefined
  });

  const modules = [
    { value: '', label: 'Todos os Módulos', icon: 'las la-globe' },
    { value: 'training', label: 'Treinamentos', icon: 'las la-play-circle' },
    { value: 'agent', label: 'Agentes', icon: 'las la-robot' },
    { value: 'marketplace', label: 'Marketplace', icon: 'las la-store' },
  ];

  const getRankBadge = (rank) => {
    if (rank === 1) return { icon: '🥇', class: 'rank-1' };
    if (rank === 2) return { icon: '🥈', class: 'rank-2' };
    if (rank === 3) return { icon: '🥉', class: 'rank-3' };
    return { icon: rank, class: 'rank-other' };
  };

  if (loading) {
    return (
      <div className="">
        <div className="card-header">
          <h3 className="card-title">
            <i className="las la-crown"></i>
            Ranking Global
          </h3>
        </div>
        <div className="loading-container" style={{ padding: '2rem' }}>
          <div className="loading-text">Carregando ranking...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="">
        <div className="card-header">
          <h3 className="card-title">
            <i className="las la-crown"></i>
            Ranking Global
          </h3>
        </div>
        <div className="error-container" style={{ padding: '2rem' }}>
          <div className="error-message" style={{ fontSize: '0.9rem' }}>
            Erro ao carregar o ranking
          </div>
          <button onClick={refresh} className="retry-button" style={{ marginTop: '1rem', fontSize: '0.8rem', padding: '0.5rem 1rem' }}>
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="card-header">
        <h3 className="card-title">
          <i className="las la-crown"></i>
          Ranking Global
        </h3>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          Top {leaderboard.length} usuários mais ativos
        </div>
      </div>

      {/* Filtros */}
      <div style={{ 
        display: 'flex', 
        gap: '1rem', 
        marginBottom: '1rem',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <select
          value={selectedModule}
          onChange={(e) => setSelectedModule(e.target.value)}
          className="form-select"
          style={{ 
            flex: 1, 
            minWidth: '200px',
            fontSize: '0.85rem'
          }}
        >
          {modules.map(module => (
            <option key={module.value} value={module.value}>
              {module.label}
            </option>
          ))}
        </select>
        
        <select
          value={limit}
          onChange={(e) => setLimit(parseInt(e.target.value))}
          className="form-select"
          style={{ 
            width: '100px',
            fontSize: '0.85rem'
          }}
        >
          <option value={25}>Top 25</option>
          <option value={50}>Top 50</option>
          <option value={100}>Top 100</option>
        </select>
      </div>

      {/* Leaderboard */}
      <div className="leaderboard-container">
        {leaderboard.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '2rem', 
            color: 'var(--text-muted)'
          }}>
            <i className="las la-users" style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}></i>
            <p>Nenhum usuário encontrado</p>
            <p style={{ fontSize: '0.9rem', marginBottom: 0 }}>
              Seja o primeiro a aparecer no ranking!
            </p>
          </div>
        ) : (
          leaderboard.map((user, index) => {
            const rankBadge = getRankBadge(user.rank);
            
            return (
              <div 
                key={user.user.id} 
                className={`leaderboard-item animate-in`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className={`rank-badge ${rankBadge.class}`}>
                  {rankBadge.icon}
                </div>
                
                {user.user.photo ? (
                  <img 
                    src={user.user.photo} 
                    alt={user.user.name}
                    className="user-avatar"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                
                <div 
                  className="user-avatar"
                  style={{
                    display: user.user.photo ? 'none' : 'flex',
                    background: 'var(--primary)',
                    color: 'white',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}
                >
                  {user.user.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                
                <div className="user-info">
                  <div className="user-name">
                    {user.user.name || 'Usuário Anônimo'}
                  </div>
                  <div className="user-level">
                    <span>Nível {user.level} • </span>
                    <TierBadge 
                      tier={{ tier: user.tier, name: user.tier_name }} 
                      size="sm" 
                      showIcon={false}
                    />
                  </div>
                </div>
                
                <div className="user-xp">
                  {GamificationServices.formatXP(user.xp)}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Ações */}
      {leaderboard.length > 0 && (
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          marginTop: '1rem',
          paddingTop: '1rem',
          borderTop: '1px solid var(--border-color)',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <button 
            onClick={refresh}
            className="btn btn-outline-primary btn-sm"
          >
            <i className="las la-sync-alt me-1"></i>
            Atualizar
          </button>
          
          <button 
            className="btn btn-outline-secondary btn-sm"
            onClick={() => {
              // Implementar exportação do ranking
              console.log('Export ranking feature');
            }}
          >
            <i className="las la-download me-1"></i>
            Exportar
          </button>
        </div>
      )}

      {/* Estatísticas do ranking */}
      {selectedModule === '' && leaderboard.length > 0 && (
        <div style={{ 
          marginTop: '1rem', 
          padding: '0.75rem', 
          background: 'var(--surface)', 
          borderRadius: '8px',
          fontSize: '0.8rem',
          color: 'var(--text-muted)',
          textAlign: 'center'
        }}>
          <i className="las la-info-circle me-1"></i>
          Mostrando o ranking geral de todos os módulos da plataforma
        </div>
      )}
    </div>
  );
};

export default LeaderboardCard;